import {css} from '@emotion/react';
import {useTheme} from '@mui/material';
import {DlMuiDynamicIcon, GetStylesFunction, RemoveButton, TranslateUtils} from '@wallaby-ui/core';
import classNames from 'classnames';
import * as React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {NavigationBoxDivider} from './NavigationBoxDivider';
import styles from './NavigationHeader.less';
import {RouteGroup} from '../useNavigationContext';

const I18N = defineMessages({
  GROUP_LABEL: {
    id: 'navigation.routeGroup.openedCases',
    defaultMessage: 'OPENED CASES',
    description: 'text for navigation group label',
  },
  REMOVE_BUTTON: {
    id: 'navigation.button.removeAll',
    defaultMessage: 'Close all',
    description: 'text for navigation remove button',
  },
});

interface OwnProps {
  isNavigationOpen: boolean;
  isOpened?: boolean;
  routeGroup: RouteGroup;
  removeDynamicItemGroup?: (group: RouteGroup) => void;
}

type Props = OwnProps;

export const NavigationHeader: React.FC<Props> = (props) => {
  const {isNavigationOpen, isOpened = false, routeGroup, removeDynamicItemGroup} = props;
  const theme = useTheme();

  const navigationHeaderStyle = getStyles({
    theme,
    params: {isOpened},
  });

  const intl = useIntl();

  const headerLabel = routeGroup.label;
  return headerLabel ? (
    <>
      <NavigationBoxDivider isNavigationOpen={isNavigationOpen} />
      <div
        className={classNames(styles.dlNavigationHeaderWrapper, {
          [styles.dlNavigationHeaderWrapperHidden]: !isNavigationOpen,
        })}
      >
        <div data-name={'navigation-header--content'} css={navigationHeaderStyle.navigationHeader}>
          {isOpened ? (
            <span className={styles.dlNavigationHeaderLabel}>{TranslateUtils.translate({...I18N.GROUP_LABEL, id: headerLabel}, intl)}</span>
          ) : (
            <DlMuiDynamicIcon
              data-name={'navigation-header--list-icon'}
              iconName='listIcon'
              // useFixedViewBox='0 0 16 17'
              css={navigationHeaderStyle.navigationLabelIcon}
            />
          )}
          {removeDynamicItemGroup && isOpened ? (
            <span
              data-name={'navigation-header--remove-item-wrapper'}
              css={navigationHeaderStyle.removeItem}
              className={classNames(styles.dlRemoveItemPadding)}
            >
              <RemoveButton
                className={styles.dlRemoveItemButton}
                item={routeGroup as RouteGroup}
                removeCallback={() => removeDynamicItemGroup(routeGroup as RouteGroup)}
                titleText={I18N.REMOVE_BUTTON.id}
              />
            </span>
          ) : null}
        </div>
      </div>
    </>
  ) : null;
};

NavigationHeader.displayName = 'NavigationHeader';

export const REMOVE_ITEM_RIGHT_OFFSET = '4px';
const getStyles: GetStylesFunction = (options) => ({
  navigationLabelIcon: css({
    width: '16px',
    height: '16px',
    color: `${options?.theme?.palette.custom.navigationHeader}`,
  }),
  navigationHeader: css({
    padding: `${options?.params?.isOpened ? '0 12px 0 15px' : '0 14px'}`,
    justifyContent: `${options?.params?.isOpened ? 'flex-start' : 'center'}`,
    fontSize: 'var(--dlFontSizeNormal)',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    lineHeight: '17.6px',
    color: `${options?.theme?.palette.custom.navigationHeader}`,
  }),
  removeItem: css({
    right: REMOVE_ITEM_RIGHT_OFFSET,
    color: `${options?.theme?.palette.custom.removeItemIcon}`,
    position: 'absolute',
    top: '50%',
    transform: 'translate(0, -50%)',
    fontSize: 'var(--dlFontSizeNormal)',
  }),
});
